/**
 * Produtos e Serviços (DEMO-03, telas/Administracao.tsx › Produtos), agora
 * persistente: PN/SKU, descrição, fabricante, categoria, pilar, tipo,
 * situação, observações. Duplicidade conferida por fabricante + PN
 * normalizados (mostra o existente em vez de duplicar). Alterar o cadastro
 * NÃO muda Propostas enviadas/aceitas (elas guardam a cópia histórica).
 * Importação .xlsx: o servidor guarda o arquivo, valida, mostra a prévia e, na
 * confirmação, reanalisa o mesmo arquivo contra o catálogo atual.
 */
import { usePersonalizacao } from './personalizacao.tsx';
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useRota } from '../rotas.ts';
import { api, consulta } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { Ajuda, CampoBusca, Estado, EstadoCarga, Modal, Nota, ResumoIndicadores, SeloSituacao, Th, useCarga, useOrdenacao } from './ui.tsx';
import { BarraFiltros, filtroSelecao } from './filtros.tsx';
import { Icone } from '../componentes/icones.tsx';
import { exigePn, exportarCatalogo, gerarModeloProdutos, selecaoPadrao, selecionavel, textoRelatorio, TIPOS_ITEM, validarProduto, type Moeda, type PreviaImportacao, type Produto, type RelatorioImportacao, type SituacaoImportacao, type TipoItem } from '../../../backend/src/compartilhado/produtos.ts';

type ProdutoApi = Produto & { atualizado_em: string; migrado?: boolean };
interface Opcoes {
  pilares: string[];
  pilares_configurados: boolean;
  limites: { bytes: number; linhas: number };
}
const hoje = () => new Date().toISOString().slice(0, 10);

type ColProduto = 'sku' | 'descricao' | 'tipo' | 'fabricante' | 'categoria' | 'pilar' | 'moeda' | 'situacao';

/* P5-07: filtros e busca ficam na sessão do navegador (ir a outra tela e voltar não os perde); "Limpar filtros" apaga */
interface FiltrosProdutos {
  busca: string;
  situacao: '' | 'Ativo' | 'Inativo';
  tipo: string;
  pilar: string;
  fabricante: string;
  moeda: string;
}
const FILTROS_PADRAO: FiltrosProdutos = { busca: '', situacao: 'Ativo', tipo: '', pilar: '', fabricante: '', moeda: '' };
const CHAVE_FILTROS = 'inttrix:produtos:filtros';
function lerFiltros(): FiltrosProdutos | null {
  try {
    const x = JSON.parse(sessionStorage.getItem(CHAVE_FILTROS) ?? 'null') as Partial<FiltrosProdutos> | null;
    return x ? { ...FILTROS_PADRAO, ...x } : null;
  } catch {
    return null;
  }
}
function gravarFiltros(f: FiltrosProdutos) {
  try {
    if ((Object.keys(FILTROS_PADRAO) as (keyof FiltrosProdutos)[]).every((k) => f[k] === FILTROS_PADRAO[k])) sessionStorage.removeItem(CHAVE_FILTROS);
    else sessionStorage.setItem(CHAVE_FILTROS, JSON.stringify(f));
  } catch {
    /* sem armazenamento da sessão: os filtros só não sobrevivem à navegação */
  }
}
const ordenarTextos = (xs: string[]) => [...new Set(xs.filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR'));

export function Produtos() {
  const { pode } = useSessao();
  const rota = useRota();
  const skuPedido = new URLSearchParams(rota.split('?')[1] ?? '').get('sku') ?? '';
  const guardados = useMemo(lerFiltros, []);
  const inicial = guardados ?? FILTROS_PADRAO;
  const [busca, setBusca] = useState(skuPedido || inicial.busca);
  // Resultado da busca global (F1-06): abre a lista já filtrada pelo PN/SKU escolhido.
  useEffect(() => {
    if (skuPedido) setBusca(skuPedido);
  }, [skuPedido]);
  const [editar, setEditar] = useState<ProdutoApi | 'novo' | null>(null);
  const [importar, setImportar] = useState(false);
  const [situacao, setSituacao] = useState<'' | 'Ativo' | 'Inativo'>(inicial.situacao);
  const [tipo, setTipo] = useState(inicial.tipo);
  const [pilar, setPilar] = useState(inicial.pilar);
  const [fabricante, setFabricante] = useState(inicial.fabricante);
  const [moeda, setMoeda] = useState(inicial.moeda);
  useEffect(() => gravarFiltros({ busca, situacao, tipo, pilar, fabricante, moeda }), [busca, situacao, tipo, pilar, fabricante, moeda]);
  const ord = useOrdenacao<ColProduto>('sku');
  // P2-03/P2-04 (D7)
  const pers = usePersonalizacao('produtos', { busca: { valor: busca, definir: setBusca },
    filtros: { situacao, tipo, pilar, fabricante, moeda },
    aplicarFiltros: (e: Record<string, unknown>) => (setSituacao((e.situacao as '' | 'Ativo' | 'Inativo') ?? 'Ativo'), setTipo((e.tipo as string) ?? ''), setPilar((e.pilar as string) ?? ''), setFabricante((e.fabricante as string) ?? ''), setMoeda((e.moeda as string) ?? '')),
    ordenacao: ord,
    // voltando à lista na mesma sessão, os filtros guardados valem sobre os da visão padrão (as colunas da visão continuam)
    manterFiltrosAoAbrir: guardados !== null || skuPedido !== '',
  });
  const carga = useCarga(() => Promise.all([api.get<ProdutoApi[]>(`/api/produtos${consulta({ busca: busca.trim() })}`), api.get<Opcoes>('/api/produtos/opcoes')]), [busca]);
  const [todos, opcoes] = carga.dados ?? [[], null];
  const recarregar = () => void carga.recarregar();
  // P5-07: filtros dependentes — as opções (e os contadores de status) de cada critério vêm do conjunto filtrado pelos
  // DEMAIS critérios ativos; nunca se oferece um valor sem resultado, mas o valor escolhido continua visível
  type Criterio = 'situacao' | 'tipo' | 'pilar' | 'fabricante' | 'moeda';
  const atende = (p: ProdutoApi, exceto?: Criterio) =>
    (exceto === 'situacao' || !situacao || p.situacao === situacao) &&
    (exceto === 'tipo' || !tipo || p.tipoItem === tipo) &&
    (exceto === 'pilar' || !pilar || p.pilar === pilar) &&
    (exceto === 'fabricante' || !fabricante || p.fabricante === fabricante) &&
    (exceto === 'moeda' || !moeda || p.moeda === moeda);
  const semSituacao = todos.filter((p) => atende(p, 'situacao'));
  const conta = (x: string) => semSituacao.filter((p) => p.situacao === x).length;
  const valores = (c: Exclude<Criterio, 'situacao'>, f: (p: ProdutoApi) => string, atual: string) => ordenarTextos([...todos.filter((p) => atende(p, c)).map(f), atual]).map((x) => ({ id: x, nome: x }));
  const tiposDisponiveis = new Set(todos.filter((p) => atende(p, 'tipo')).map((p) => p.tipoItem as string));
  const filtrado = todos.filter((p) => atende(p));
  const lista = ord.ordenar(filtrado, {
    sku: (p) => p.sku,
    descricao: (p) => p.nome,
    tipo: (p) => p.tipoItem,
    fabricante: (p) => p.fabricante,
    categoria: (p) => p.categoria,
    pilar: (p) => p.pilar,
    moeda: (p) => p.moeda,
    situacao: (p) => p.situacao,
  });
  const comFiltro = busca.trim() || tipo || pilar || fabricante || moeda || (situacao && todos.length);
  return (
    <>
      {/* F1-42/F1-46: a trilha é o título; filtros progressivos e ordenação pelo cabeçalho, como nas demais listas */}
      <div className="titulo-pag-linha cabecalho-fixo" data-teste="cabecalho-produtos">
        <span className="linha-totais" data-teste="resumo-produtos">
          <b>{carga.dados ? lista.length : '…'}</b> produto(s) e serviço(s)
        </span>
        <Ajuda titulo="Catálogo">Catálogo usado nas Propostas (busca pelo PN/SKU). Cadastro rápido na Proposta conforme a permissão do usuário. Propostas enviadas ou aceitas guardam uma cópia histórica: alterar este cadastro não muda documentos antigos.</Ajuda>
        <span className="espaco" />
        <button type="button" className="botao-secundario" onClick={async () => baixarArquivo(exportarCatalogo(await api.get<ProdutoApi[]>('/api/produtos')), `catalogo-produtos-servicos-${hoje()}.xlsx`)} data-teste="exportar-produtos">
          Exportar catálogo (.xlsx)
        </button>
        {pode('importar', 'produto') && (
          <button type="button" className="botao-secundario" onClick={() => setImportar(true)} data-teste="importar-produtos">
            Importar planilha
          </button>
        )}
        {pode('criar', 'produto') && (
          <button type="button" className="botao-primario" onClick={() => setEditar('novo')} data-teste="novo-produto">
            <Icone nome="mais" /> Novo produto
          </button>
        )}
      </div>
      {opcoes && !opcoes.pilares_configurados && (
        <p className="aviso aviso-alerta" role="alert">
          A lista de Pilares não está configurada neste ambiente. Peça ao Administrador para configurá-la em Parâmetros.
        </p>
      )}
      <BarraFiltros
        teste="produtos"
        direita={pers.controles}
        fixos={
          <>
            <CampoBusca valor={busca} setValor={setBusca} rotulo="Buscar produto" ajuda="PN/SKU, descrição ou fabricante" dica="PN/SKU, descrição, fabricante, categoria ou pilar" teste="busca-produtos" total={busca.trim() ? lista.length : null} />
            <label>
              Status
              <select value={situacao} onChange={(e) => setSituacao(e.target.value as '' | 'Ativo' | 'Inativo')} data-teste="filtro-situacao-produto">
                <option value="Ativo">Ativos ({conta('Ativo')})</option>
                <option value="Inativo">Inativos ({conta('Inativo')})</option>
                <option value="">Todos ({semSituacao.length})</option>
              </select>
            </label>
            <label>
              Tipo do item
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-teste="filtro-tipo-produto">
                <option value="">Todos</option>
                {TIPOS_ITEM.filter((t) => t === tipo || tiposDisponiveis.has(t)).map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </label>
          </>
        }
        opcionais={[
          filtroSelecao('pilar', 'Pilar', pilar, setPilar, valores('pilar', (p) => p.pilar, pilar), 'filtro-pilar-produto'),
          filtroSelecao('fabricante', 'Fabricante', fabricante, setFabricante, valores('fabricante', (p) => p.fabricante, fabricante), 'filtro-fabricante-produto'),
          filtroSelecao('moeda', 'Moeda', moeda, setMoeda, valores('moeda', (p) => p.moeda, moeda), 'filtro-moeda-produto'),
        ]}
        aoLimpar={() => (setSituacao('Ativo'), setTipo(''), setPilar(''), setFabricante(''), setMoeda(''), setBusca(''), gravarFiltros(FILTROS_PADRAO))}
      />
      <EstadoCarga carregando={carga.carregando && !carga.dados} erro={carga.erro} recarregar={carga.recarregar} teste="produtos" />
      {carga.dados &&
        (lista.length === 0 ? (
          <Estado tipo="vazio" titulo={comFiltro ? (busca.trim() ? `nenhum produto com “${busca}”` : 'nenhum produto com estes filtros') : 'nenhum produto cadastrado'} teste="produtos-vazio" />
        ) : (
          <div className="rolagem quadro-tabela">
            <table ref={pers.tabela} className="tabela tabela-compacta tabela-produtos" data-teste="lista-produtos">
              <thead>
                <tr>
                  <Th ord={ord} coluna="sku" className="col-fixa">
                    PN/SKU
                  </Th>
                  <Th ord={ord} coluna="descricao">Descrição</Th>
                  <Th ord={ord} coluna="tipo">Tipo</Th>
                  <Th ord={ord} coluna="fabricante">Fabricante</Th>
                  <Th ord={ord} coluna="categoria">Categoria</Th>
                  <Th ord={ord} coluna="pilar">Pilar</Th>
                  <Th ord={ord} coluna="moeda">Moeda</Th>
                  <Th ord={ord} coluna="situacao">Status</Th>
                  <th className="col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((p) => (
                  <tr key={p.id} data-teste={`produto-${p.sku || p.id}`}>
                    <td className="nowrap col-fixa">{p.sku || <span className="ausente">— (serviço sem PN)</span>}</td>
                    <td>
                      <DescricaoProduto nome={p.nome} observacoes={p.observacoes} />
                    </td>
                    <td>{p.tipoItem}</td>
                    <td>{p.fabricante || <span className="ausente">—</span>}</td>
                    <td>{p.categoria || <span className="ausente">—</span>}</td>
                    <td>{p.pilar || <span className="ausente">—</span>}</td>
                    <td>{p.moeda}</td>
                    <td>
                      <SeloSituacao texto={p.situacao} tom={p.situacao === 'Ativo' ? 'positivo' : 'encerrado'} />
                    </td>
                    <td className="col-acoes">
                      {pode('editar', 'produto') && (
                        <button type="button" className="botao-secundario pequeno" onClick={() => setEditar(p)}>
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="rodape-tabela">{lista.length === 1 ? '1 item' : `${lista.length} itens`}</div>
          </div>
        ))}
      {editar && opcoes && <ModalProduto produto={editar === 'novo' ? undefined : editar} pilares={opcoes.pilares} aoFechar={() => setEditar(null)} aoSalvar={() => (setEditar(null), recarregar())} />}
      {importar && opcoes && <ModalImportarProdutos pilares={opcoes.pilares} aoFechar={() => (setImportar(false), recarregar())} />}
    </>
  );
}

/**
 * F1-42 + P5-07: descrição longa em até 2 linhas (texto completo na dica) e "ver descrição completa", um botão de
 * verdade (teclado e toque), que expande na própria linha; só aparece quando há texto escondido.
 */
function DescricaoProduto({ nome, observacoes }: { nome: string; observacoes?: string | null }) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  const [aberta, setAberta] = useState(false);
  const [cortada, setCortada] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || aberta) return;
    const medir = () => setCortada(el.scrollHeight > el.clientHeight + 1);
    medir();
    const obs = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    obs?.observe(el);
    return () => obs?.disconnect();
  }, [nome, aberta]);
  const temMais = cortada || Boolean(observacoes);
  return (
    <>
      <div ref={ref} id={id} className={aberta ? 'descricao-aberta' : 'texto-2-linhas'} title={[nome, observacoes].filter(Boolean).join(' — ')} data-teste="descricao-produto">
        {nome}
        {aberta && observacoes && <small className="bloco texto-secundario">{observacoes}</small>}
      </div>
      {(temMais || aberta) && (
        <button type="button" className="botao-link pequeno ver-descricao" aria-expanded={aberta} aria-controls={id} onClick={() => setAberta(!aberta)} data-teste="ver-descricao">
          {aberta ? 'ver menos' : 'ver descrição completa'}
        </button>
      )}
    </>
  );
}

const TIPO_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export function baixarArquivo(bytes: Uint8Array, nome: string) {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: TIPO_XLSX }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const paraBase64 = (b: Uint8Array) => {
  let s = '';
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(s);
};

const ROTULO_SITUACAO_IMP: Record<SituacaoImportacao, string> = { novo: 'Novo', atualizacao: 'Possível atualização', possivel_duplicado: 'Possível duplicado', sem_alteracao: 'Sem alteração', erro: 'Erro' };

/**
 * Importar produtos/serviços (.xlsx): baixar modelo → preencher → enviar → validar → prévia →
 * confirmar → importar → relatório. Novos vêm marcados; possíveis atualizações e possíveis
 * duplicados vêm DESMARCADOS (só entram com seleção explícita). Linhas com erro nunca entram.
 */
function ModalImportarProdutos({ pilares, aoFechar }: { pilares: string[]; aoFechar: () => void }) {
  const [arquivo, setArquivo] = useState('');
  const [previa, setPrevia] = useState<(PreviaImportacao & { importacao_id?: string }) | null>(null);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [relatorio, setRelatorio] = useState<RelatorioImportacao | null>(null);
  const [lendo, setLendo] = useState(false);
  const [erro, setErro] = useState('');
  const ler = async (f: File) => {
    setLendo(true);
    setRelatorio(null);
    setErro('');
    try {
      const p = await api.post<PreviaImportacao & { importacao_id: string }>('/api/produtos/importacoes', { nome: f.name, conteudo_base64: paraBase64(new Uint8Array(await f.arrayBuffer())) });
      setArquivo(f.name);
      setPrevia(p);
      setSel(selecaoPadrao(p));
    } catch (e) {
      setArquivo(f.name);
      setPrevia(null);
      setErro((e as Error).message);
    } finally {
      setLendo(false);
    }
  };
  const conta = (s: SituacaoImportacao) => previa?.linhas.filter((l) => l.situacao === s).length ?? 0;
  const alternar = (n: number) => setSel((x) => (x.has(n) ? new Set([...x].filter((y) => y !== n)) : new Set([...x, n])));
  const confirmar = async () => {
    if (!previa?.importacao_id) return;
    setErro('');
    try {
      const r = await api.post<{ relatorio: RelatorioImportacao }>(`/api/produtos/importacoes/${previa.importacao_id}/confirmar`, { linhas: [...sel] });
      setRelatorio(r.relatorio);
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo="Importar produtos/serviços (.xlsx)"
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            {relatorio ? 'Fechar' : 'Cancelar'}
          </button>
          {!relatorio && (
            <button type="button" className="botao-primario" disabled={!previa || Boolean(previa.erroArquivo) || sel.size === 0} onClick={confirmar} data-teste="confirmar-importacao-produtos">
              Importar selecionados ({sel.size})
            </button>
          )}
        </>
      }
    >
      <ol className="passos-importacao">
        <li>
          Baixe o modelo{' '}
          <button type="button" className="botao-link" onClick={() => baixarArquivo(gerarModeloProdutos(pilares), 'modelo-produtos-servicos.xlsx')} data-teste="baixar-modelo-produtos">
            modelo-produtos-servicos.xlsx
          </button>{' '}
          (ou exporte o catálogo atual, no mesmo formato).
        </li>
        <li>Preencha a partir da linha 3. PN/SKU é obrigatório para Produto (Serviço pode ficar sem PN). As validações são as mesmas do cadastro manual.</li>
        <li>
          Envie o arquivo: <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => e.target.files?.[0] && void ler(e.target.files[0])} data-teste="arquivo-produtos" />
          {lendo && <span className="texto-secundario"> validando…</span>}
        </li>
        <li>Confira a prévia e confirme. Nada é gravado antes da confirmação; nenhum cadastro existente é sobrescrito sem seleção explícita.</li>
      </ol>
      {erro && <Estado tipo="bloqueado" titulo="Arquivo recusado" itens={[erro]} teste="erro-arquivo-produtos" />}
      {previa?.erroArquivo && <Estado tipo="bloqueado" titulo="Arquivo recusado" itens={[previa.erroArquivo]} teste="erro-arquivo-produtos" />}
      {previa && !previa.erroArquivo && (
        <>
          <ResumoIndicadores
            compacto
            teste="resumo-previa-produtos"
            itens={[
              { rotulo: 'Arquivo', valor: arquivo },
              { rotulo: 'Novos', valor: conta('novo') },
              { rotulo: 'Possíveis atualizações', valor: conta('atualizacao') },
              { rotulo: 'Possíveis duplicados', valor: conta('possivel_duplicado') },
              { rotulo: 'Sem alteração', valor: conta('sem_alteracao') },
              { rotulo: 'Com erro', valor: conta('erro') },
            ]}
          />
          <div className="rolagem">
            <table className="tabela tabela-compacta" data-teste="previa-produtos">
              <thead>
                <tr>
                  <th>Linha</th>
                  <th>Situação</th>
                  <th className="col-pn">PN/SKU</th>
                  <th className="col-descricao">Descrição</th>
                  <th>Tipo</th>
                  <th>Detalhe</th>
                  <th>Importar?</th>
                </tr>
              </thead>
              <tbody>
                {previa.linhas.map((l) => (
                  <tr key={l.linha} className={`imp-${l.situacao}`} data-teste={`previa-linha-${l.linha}`} data-situacao={l.situacao}>
                    <td>{l.linha}</td>
                    <td>
                      <span className={`selo ${l.situacao === 'erro' ? 'selo-alerta' : l.situacao === 'novo' ? 'selo-confirmado' : 'selo-neutro'}`}>{ROTULO_SITUACAO_IMP[l.situacao]}</span>
                    </td>
                    <td className="nowrap">{l.produto.sku || '—'}</td>
                    <td className="descricao-produto">{l.produto.nome || '—'}</td>
                    <td>{l.produto.tipoItem}</td>
                    <td>
                      {l.erros.length > 0 && <span className="erro-campo">{l.erros.join(' ')}</span>}
                      {l.diferencas?.map((x) => (
                        <div key={x.campo} className="texto-pequeno">
                          {x.campo}: <s>{x.antes || '—'}</s> → <strong>{x.depois || '—'}</strong>
                        </div>
                      ))}
                      {l.motivo && <span className="texto-secundario">{l.motivo}</span>}
                      {l.situacao === 'sem_alteracao' && <span className="texto-secundario">igual ao cadastro</span>}
                    </td>
                    <td>
                      {selecionavel(l) ? (
                        <label className="opcao nowrap">
                          <input type="checkbox" checked={sel.has(l.linha)} disabled={Boolean(relatorio)} onChange={() => alternar(l.linha)} data-teste={`selecionar-linha-${l.linha}`} /> {l.situacao === 'atualizacao' ? 'atualizar' : 'incluir'}
                        </label>
                      ) : (
                        <span className="texto-secundario">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {relatorio && (
        <Estado tipo="concluido" titulo={`Importação concluída: ${textoRelatorio(relatorio)}`} itens={previa?.linhas.filter((l) => l.situacao === 'erro').map((l) => `Linha ${l.linha}: ${l.erros.join(' ')}`)} teste="relatorio-importacao-produtos" />
      )}
    </Modal>
  );
}

/** Cadastro (completo ou rápido, a partir da Proposta). Antes de salvar, o servidor confere duplicidade por fabricante + PN. */
export function ModalProduto({ produto, inicial, pilares, aoFechar, aoSalvar, rapido }: { produto?: ProdutoApi; inicial?: Partial<Produto>; pilares: string[]; aoFechar: () => void; aoSalvar?: (p: ProdutoApi) => void; rapido?: boolean }) {
  const [p, setP] = useState<Produto>(() => (produto ? { ...produto } : { id: '', sku: '', nome: '', fabricante: '', unidade: 'UN', moeda: 'BRL', categoria: '', pilar: '', tipoItem: 'Produto', situacao: 'Ativo', observacoes: '', ...inicial }));
  const [tentou, setTentou] = useState(false);
  const [dup, setDup] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const migrado = Boolean(produto?.migrado);
  const faltam = [...validarProduto(p, pilares), ...(migrado && !justificativa.trim() ? ['Justificativa (registro migrado da planilha)'] : [])];
  const salvar = async () => {
    setTentou(true);
    setDup('');
    if (faltam.length) return;
    try {
      const final = produto ? await api.patch<ProdutoApi>(`/api/produtos/${produto.id}`, { ...p, justificativa: justificativa.trim() || undefined, atualizado_em: produto.atualizado_em }) : await api.post<ProdutoApi>('/api/produtos', p);
      aoSalvar?.(final);
    } catch (e) {
      setDup((e as Error).message);
    }
  };
  const campo = (k: keyof Produto, rotulo: string, largo = false) => (
    <label className={largo ? 'coluna-dupla' : undefined}>
      {rotulo}
      <input value={String(p[k] ?? '')} onChange={(e) => (setP({ ...p, [k]: e.target.value }), setDup(''))} data-teste={`produto-${k}`} />
    </label>
  );
  return (
    <Modal
      titulo={produto ? `Editar produto — ${produto.sku || produto.nome}` : rapido ? 'Cadastro rápido de produto (permissão: cadastrar produto)' : 'Novo produto ou serviço'}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" onClick={salvar} data-teste="salvar-produto">
            {produto ? 'Salvar alterações' : 'Cadastrar'}
          </button>
        </>
      }
    >
      {produto && <p className="aviso aviso-info">Alterar o cadastro não muda Propostas enviadas ou aceitas, Pedidos e Ordens: eles guardam a cópia histórica do produto.</p>}
      <div className="grade-form sem-borda">
        {campo('sku', exigePn(p.tipoItem) ? 'PN/SKU *' : 'PN/SKU (opcional para Serviço)')}
        {campo('fabricante', 'Fabricante')}
        {campo('nome', 'Descrição', true)}
        {campo('categoria', 'Categoria')}
        <label>
          Pilar
          <select value={p.pilar} onChange={(e) => setP({ ...p, pilar: e.target.value })} data-teste="produto-pilar">
            <option value="">Selecione…</option>
            {pilares.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label>
          Tipo do item
          <select value={p.tipoItem} onChange={(e) => setP({ ...p, tipoItem: e.target.value as TipoItem })} data-teste="produto-tipo">
            {TIPOS_ITEM.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label>
          Moeda de custo
          <select value={p.moeda} onChange={(e) => setP({ ...p, moeda: e.target.value as Moeda })}>
            <option>BRL</option>
            <option>USD</option>
          </select>
        </label>
        <label>
          Status
          <select value={p.situacao} onChange={(e) => setP({ ...p, situacao: e.target.value as Produto['situacao'] })}>
            <option>Ativo</option>
            <option>Inativo</option>
          </select>
        </label>
        {campo('observacoes', 'Observações', true)}
        {migrado && (
          <label className="coluna-dupla">
            Justificativa (obrigatória: registro migrado da planilha)
            <textarea rows={2} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} maxLength={1000} data-teste="justificativa-produto" />
          </label>
        )}
      </div>
      {dup && (
        <p className="aviso aviso-alerta" role="alert" data-teste="produto-duplicado">
          <span className="rotulo-nivel">▲ Atenção</span>
          {dup}
        </p>
      )}
      {tentou && faltam.length > 0 && <Estado tipo="bloqueado" titulo="Falta completar" itens={faltam} teste="faltam-produto" />}
    </Modal>
  );
}
